import React, { useCallback, useEffect, useRef, useState } from 'react';
import lineLogo from '../../../../assets/channels/line.svg';
import telegramLogo from '../../../../assets/channels/telegram.svg';
import viberLogo from '../../../../assets/channels/viber.jpeg';
import { SMS_ENABLED_DIAL_CODES } from '../../../../constants/otp.const';
import { ayeApi } from '../../../../core';

type OtpVerificationStatus =
  | 'idle'
  | 'sent'
  | 'verifying'
  | 'verified'
  | 'error';

interface InlineOtpVerificationProps {
  type: 'phone' | 'email';
  /** For phone: the phone number (without country code) */
  phoneNumber?: string;
  /** For phone: the country code (digits only, no '+') */
  countryCode?: string;
  /** For email: the email address */
  email?: string;
  /** Called with the verification token once OTP is successfully verified */
  onVerified: (token: string) => void;
  /** Called when the verification is reset (e.g. value changed externally) */
  onReset?: () => void;
  /** Pre-verified token from checkout flow — starts widget in verified state, skipping OTP entirely */
  preVerifiedToken?: string;
  /** Increment this value to trigger an immediate auto-send eligibility check (used on blur). */
  autoSendSignal?: number;
}

const COOLDOWN_SECONDS = 60;
const OTP_LENGTH = 6;

/**
 * Inline OTP verification widget embedded directly in the registration form.
 * The parent field (phone or email) should be filled before using this.
 */
export function InlineOtpVerification({
  type,
  phoneNumber,
  countryCode,
  email,
  onVerified,
  onReset,
  preVerifiedToken,
  autoSendSignal = 0
}: Readonly<InlineOtpVerificationProps>) {
  const idPrefix = `reg-${type}-otp`;

  const [status, setStatus] = useState<OtpVerificationStatus>('idle');
  const [otpDigits, setOtpDigits] = useState<string[]>(
    new Array(OTP_LENGTH).fill('')
  );
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
  const [cooldown, setCooldown] = useState<number>(0);
  const [sending, setSending] = useState(false);
  const [otpChannel, setOtpChannel] = useState<'sms' | 'whatsapp' | null>(
    type === 'email' ? 'sms' : null
  );

  const cooldownRef = useRef<NodeJS.Timeout | null>(null);
  const sendingSignatureRef = useRef<string>('');
  const lastSentSignatureRef = useRef<string>('');
  const sendingRef = useRef(false);
  // Signals that the user just picked a channel pill → trigger send on next render
  const channelJustPickedRef = useRef(false);

  // Track the value that was used to send OTP — reset when it changes
  const sentForRef = useRef<string>('');
  // Refs so the reset effect can read latest state without being in its deps
  const statusRef = useRef(status);
  statusRef.current = status;
  const isAlreadyRegisteredRef = useRef(isAlreadyRegistered);
  isAlreadyRegisteredRef.current = isAlreadyRegistered;

  const currentValue =
    type === 'phone' ? `${countryCode}:${phoneNumber}` : (email ?? '');
  const normalizedCountryCode = (countryCode ?? '').replace('+', '');
  const isSmsDisabledForCountry =
    type === 'phone' &&
    Boolean(countryCode) &&
    !SMS_ENABLED_DIAL_CODES.has(normalizedCountryCode);
  const currentSignature =
    type === 'phone'
      ? `phone:${countryCode ?? ''}:${phoneNumber ?? ''}:${otpChannel ?? ''}`
      : `email:${email ?? ''}`;
  const deliveryLabel =
    type === 'email' ? 'email' : otpChannel === 'whatsapp' ? 'WhatsApp' : 'SMS';

  const resetWidget = useCallback(() => {
    setStatus('idle');
    setOtpDigits(new Array(OTP_LENGTH).fill(''));
    setErrorMsg('');
    setIsAlreadyRegistered(false);
    setCooldown(0);
    if (type === 'phone') {
      setOtpChannel(null);
    }
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    sentForRef.current = '';
    sendingSignatureRef.current = '';
    lastSentSignatureRef.current = '';
    onReset?.();
  }, [onReset, type]);

  // If the phone/email value changes after OTP was sent, reset the widget
  useEffect(() => {
    if (
      (statusRef.current !== 'idle' || isAlreadyRegisteredRef.current) &&
      statusRef.current !== 'verified' &&
      sentForRef.current &&
      sentForRef.current !== currentValue
    ) {
      resetWidget();
    }
  }, [currentValue, resetWidget]);

  // When phone number or country code changes, clear channel so a stale selection
  // doesn't auto-send OTP with the new number unexpectedly.
  useEffect(() => {
    if (type !== 'phone') return;
    setOtpChannel(null);
    channelJustPickedRef.current = false;
  }, [currentValue, type]);

  // If a non-MY country is selected while SMS was active, clear the channel.
  useEffect(() => {
    if (type !== 'phone') return;
    if (!isSmsDisabledForCountry) return;
    if (otpChannel !== 'sms') return;
    setOtpChannel(null);
  }, [type, isSmsDisabledForCountry, otpChannel]);

  // If a pre-verified token is provided (phone already verified in checkout flow),
  // initialise widget as verified and propagate the token to the parent.
  // Depends on preVerifiedToken so it fires when the value arrives asynchronously
  // (RegisterScreen reads it from sessionStorage in a useEffect, so it starts undefined).
  useEffect(() => {
    if (preVerifiedToken && status === 'idle') {
      setStatus('verified');
      onVerified(preVerifiedToken);
    }
  }, [preVerifiedToken, onVerified, status]);

  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const isValueReady =
    type === 'phone'
      ? !!(phoneNumber && phoneNumber.length >= 5 && countryCode)
      : !!(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

  const isReadyForAutoSend =
    isValueReady && (type === 'email' || Boolean(otpChannel));

  const sendOTP = useCallback(
    async (force = false) => {
      if (!isReadyForAutoSend || sendingRef.current) return;
      // Guard: skip if we already attempted or succeeded for this exact signature
      // (force=true bypasses this, used for manual Resend clicks)
      if (!force && lastSentSignatureRef.current === currentSignature) {
        return;
      }
      if (sendingSignatureRef.current === currentSignature) {
        return;
      }

      sendingSignatureRef.current = currentSignature;
      sendingRef.current = true;
      setSending(true);
      setErrorMsg('');

      try {
        if (type === 'phone') {
          const api_call =
            otpChannel === 'whatsapp'
              ? ayeApi.sendRegPhoneOtpWhatsApp({
                  body: { phoneNumber: phoneNumber!, countryCode: countryCode! }
                })
              : ayeApi.sendRegPhoneOTP({
                  body: { phoneNumber: phoneNumber!, countryCode: countryCode! }
                });
          const res = await api_call;
          if (!res.success) {
            const code = res.error?.code;
            if (
              code === 'USER_ALREADY_REGISTERED' ||
              code === 'PHONE_ALREADY_IN_USE'
            ) {
              setIsAlreadyRegistered(true);
              sentForRef.current = currentValue;
            }
            // Mark as attempted so auto-send doesn't retry the same signature
            lastSentSignatureRef.current = currentSignature;
            setErrorMsg(
              res.error?.message ?? 'Failed to send OTP. Please try again.'
            );
            return;
          }
        } else {
          const res = await ayeApi.sendRegEmailOTP({
            body: { email: email! }
          });
          if (!res.success) {
            const code = res.error?.code;
            if (code === 'EMAIL_ALREADY_REGISTERED') {
              setIsAlreadyRegistered(true);
              sentForRef.current = currentValue;
            }
            // Mark as attempted so auto-send doesn't retry the same signature
            lastSentSignatureRef.current = currentSignature;
            setErrorMsg(
              res.error?.message ?? 'Failed to send OTP. Please try again.'
            );
            return;
          }
        }
        sentForRef.current = currentValue;
        lastSentSignatureRef.current = currentSignature;
        setStatus('sent');
        startCooldown();
        setOtpDigits(new Array(OTP_LENGTH).fill(''));
      } catch {
        // Mark as attempted on network errors too
        lastSentSignatureRef.current = currentSignature;
        setErrorMsg('Failed to send OTP. Please try again.');
      } finally {
        sendingSignatureRef.current = '';
        sendingRef.current = false;
        setSending(false);
      }
    },
    [
      isReadyForAutoSend,
      currentSignature,
      type,
      otpChannel,
      phoneNumber,
      countryCode,
      email,
      currentValue
    ]
  );

  // Keep a stable ref to sendOTP so effects don't need it as a dep.
  const sendOTPRef = useRef(sendOTP);
  sendOTPRef.current = sendOTP;

  // Email only: auto-send after user stops typing (debounce on currentSignature).
  useEffect(() => {
    if (type !== 'email') return;
    if (!isReadyForAutoSend) return;
    if (status !== 'idle') return;
    if (isAlreadyRegistered) return;

    const timer = setTimeout(() => {
      void sendOTPRef.current();
    }, 700);

    return () => clearTimeout(timer);
  }, [type, isReadyForAutoSend, status, isAlreadyRegistered, currentSignature]);

  // Phone: send immediately after user picks a channel pill.
  // channelJustPickedRef is set by the pill onClick handler.
  useEffect(() => {
    if (!channelJustPickedRef.current) return;
    channelJustPickedRef.current = false;
    if (!isValueReady || status !== 'idle' || isAlreadyRegistered) return;
    void sendOTPRef.current();
  }, [otpChannel, isValueReady, status, isAlreadyRegistered]);

  // Blur signal: send immediately when the parent phone/email field blurs with valid input.
  useEffect(() => {
    if (!autoSendSignal) return;
    if (!isReadyForAutoSend) return;
    if (status !== 'idle') return;
    if (isAlreadyRegistered) return;

    void sendOTPRef.current();
  }, [autoSendSignal, isReadyForAutoSend, status, isAlreadyRegistered]);

  // WebOTP API: auto-fill and auto-verify OTP from SMS on supported browsers
  useEffect(() => {
    if (status !== 'sent') return;
    if (!('OTPCredential' in window)) return;
    const ac = new AbortController();
    (
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: ac.signal
      } as CredentialRequestOptions) as Promise<{ code?: string }>
    )
      .then((credential) => {
        if (!credential?.code) return;
        const code = String(credential.code)
          .replace(/\D/g, '')
          .slice(0, OTP_LENGTH);
        const digits = code
          .split('')
          .concat(new Array(OTP_LENGTH).fill(''))
          .slice(0, OTP_LENGTH);
        setOtpDigits(digits);
        void verifyOTPDigits(digits);
      })
      .catch(() => {
        /* AbortError or unsupported — ignore */
      });
    return () => ac.abort();
  }, [status]); // eslint-disable-line

  const verifyOTPDigits = async (digits: string[]) => {
    const otp = digits.join('');
    if (otp.length !== OTP_LENGTH) return;
    setStatus('verifying');
    setErrorMsg('');

    try {
      if (type === 'phone') {
        const res = await ayeApi.verifyRegPhoneOTP({
          body: { phoneNumber: phoneNumber!, countryCode: countryCode!, otp }
        });
        if (!res.success) {
          setStatus('error');
          setErrorMsg(res.error?.message ?? 'Incorrect OTP. Please try again.');
          return;
        }
        setStatus('verified');
        if (cooldownRef.current) clearInterval(cooldownRef.current);
        onVerified(res.data.verificationToken);
      } else {
        const res = await ayeApi.verifyRegEmailOTP({
          body: { email: email!, otp }
        });
        if (!res.success) {
          setStatus('error');
          setErrorMsg(res.error?.message ?? 'Incorrect OTP. Please try again.');
          return;
        }
        setStatus('verified');
        if (cooldownRef.current) clearInterval(cooldownRef.current);
        onVerified(res.data.verificationToken);
      }
    } catch {
      setStatus('error');
      setErrorMsg('Verification failed. Please try again.');
    }
  };

  const verifyOTP = () => verifyOTPDigits(otpDigits);

  const handleDigitChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...otpDigits];
    next[index] = val;
    setOtpDigits(next);
    if (val && index < OTP_LENGTH - 1) {
      document.getElementById(`${idPrefix}-${index + 1}`)?.focus();
    }
    if (status === 'error') setStatus('sent');
    setErrorMsg('');
    if (next.join('').length === OTP_LENGTH) {
      verifyOTPDigits(next);
    }
  };

  const handleDigitKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace') {
      const next = [...otpDigits];
      next[index] = '';
      setOtpDigits(next);
      if (index > 0) {
        document.getElementById(`${idPrefix}-${index - 1}`)?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = new Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtpDigits(next);
    document
      .getElementById(`${idPrefix}-${Math.min(pasted.length, OTP_LENGTH - 1)}`)
      ?.focus();
    if (next.join('').length === OTP_LENGTH) {
      verifyOTPDigits(next);
    }
  };

  if (status === 'verified') {
    return (
      <div className="mt-2 flex items-center gap-2 text-green-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 flex-shrink-0"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
        <span className="text-sm font-medium">
          {type === 'phone' ? 'Phone number verified' : 'Email verified'}
        </span>
        <button
          type="button"
          className="ml-auto text-xs text-gray-400 underline hover:text-gray-600"
          onClick={resetWidget}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Already registered error banner */}
      {isAlreadyRegistered && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10c0 4.418-3.582 8-8 8S2 14.418 2 10 5.582 2 10 2s8 3.582 8 8zm-8-3a1 1 0 00-1 1v2a1 1 0 102 0V8a1 1 0 00-1-1zm0 6a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="text-xs font-medium text-red-700">{errorMsg}</p>
            <p className="mt-0.5 text-xs text-gray-500">
              Please enter a different{' '}
              {type === 'phone' ? 'phone number' : 'email address'} above.
            </p>
          </div>
        </div>
      )}

      {/* Send / Resend OTP button row */}
      {status === 'idle' && !isAlreadyRegistered && isValueReady && (
        <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/70 p-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
              Verification required
            </p>
          </div>

          {type === 'phone' && (
            <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-700">
                  Receive OTP via
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* WhatsApp */}
                <button
                  type="button"
                  onClick={() => {
                    channelJustPickedRef.current = true;
                    setOtpChannel('whatsapp');
                  }}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    otpChannel === 'whatsapp'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-500 hover:border-gray-400'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    viewBox="0 0 24 24"
                    fill="#25D366"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </button>

                {/* SMS */}
                <button
                  type="button"
                  disabled={isSmsDisabledForCountry}
                  onClick={() => {
                    channelJustPickedRef.current = true;
                    setOtpChannel('sms');
                  }}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    isSmsDisabledForCountry
                      ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                      : otpChannel === 'sms'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 bg-white text-gray-500 hover:border-gray-400'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z"
                    />
                  </svg>
                  SMS
                </button>

                {/* Telegram — showcase only */}
                <button
                  type="button"
                  disabled
                  className="flex cursor-not-allowed items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-400"
                >
                  <img src={telegramLogo} alt="" className="h-3.5 w-3.5" />
                  Telegram
                </button>

                {/* Line — showcase only */}
                <button
                  type="button"
                  disabled
                  className="flex cursor-not-allowed items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-400"
                >
                  <img src={lineLogo} alt="" className="h-3.5 w-3.5" />
                  Line
                </button>

                {/* Call — showcase only */}
                <button
                  type="button"
                  disabled
                  className="flex cursor-not-allowed items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-400"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C9.61 21 3 14.39 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" />
                  </svg>
                  Call
                </button>

                {/* Viber — showcase only */}
                <button
                  type="button"
                  disabled
                  className="flex cursor-not-allowed items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-400"
                >
                  <img
                    src={viberLogo}
                    alt=""
                    className="h-3.5 w-3.5 rounded-sm object-cover"
                  />
                  Viber
                </button>
              </div>
            </div>
          )}

          {errorMsg && <p className="text-xs text-red-500">{errorMsg}</p>}

          <button
            type="button"
            disabled={!isReadyForAutoSend || sending}
            onClick={() => void sendOTP(true)}
            className="w-full rounded-lg bg-[#5CB891] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#4aa07c] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? 'Sending…' : errorMsg ? 'Retry' : 'Send OTP'}
          </button>
        </div>
      )}

      {(status === 'sent' || status === 'verifying' || status === 'error') && (
        <div className="space-y-2">
          <div className="rounded-lg border border-green-100 bg-green-50 px-3 py-2">
            <p className="text-xs font-medium uppercase tracking-wide text-green-700">
              OTP sent via {deliveryLabel}
            </p>
            <p className="mt-1 text-xs text-gray-600">
              Enter the {OTP_LENGTH}-digit code to continue.
            </p>
          </div>

          {/* OTP digit inputs */}
          <div className="flex gap-2" onPaste={handlePaste}>
            {otpDigits.map((digit, i) => (
              <input
                key={i}
                id={`${idPrefix}-${i}`}
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d*"
                maxLength={1}
                value={digit}
                disabled={status === 'verifying'}
                onChange={(e) => handleDigitChange(e, i)}
                onKeyDown={(e) => handleDigitKeyDown(e, i)}
                className="h-10 w-10 rounded-lg border border-gray-400 text-center text-base shadow-sm focus:border-[#5CB891] focus:outline-none focus:ring-1 focus:ring-[#5CB891] disabled:opacity-60"
              />
            ))}
          </div>

          {/* Error message */}
          {errorMsg && <p className="text-xs text-red-500">{errorMsg}</p>}

          {/* Verify + Resend row */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              disabled={
                otpDigits.join('').length !== OTP_LENGTH ||
                status === 'verifying'
              }
              onClick={verifyOTP}
              className="rounded-lg bg-[#5CB891] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#4aa07c] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {status === 'verifying' ? 'Verifying…' : 'Verify'}
            </button>

            {cooldown > 0 ? (
              <span className="text-xs text-gray-500">
                Resend in {cooldown}s
              </span>
            ) : (
              <button
                type="button"
                disabled={sending}
                onClick={() => void sendOTP(true)}
                className="text-xs text-[#5CB891] underline disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Resend OTP'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
